import React, { Fragment, useState, useEffect } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { showHttpError, showToast, currentPageAccess } from "../utils/library";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";

const API_BASE = WEBSERVICE;
const getMerchantId = () => localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";
const getToken = () => getAuthToken() || "";

const authHeaders = () => ({
    "Content-Type": "application/json",
    "Authorization": `Bearer ${getToken()}`,
    "UUID": getAuthUuid() || "",
});

const api = {
    addDomain: (merchantId, domain, description) =>
        fetch(`${API_BASE}/domain/add`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ merchantId: String(merchantId), domain, description }),
        }).then(r => {
            if (!r.ok) throw { response: { status: r.status } };
            return r.json();
        }),
    cancelDomain: (id, merchantId) =>
        fetch(`${API_BASE}/domain/cancel`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ id, merchantId }),
        }).then(r => {
            if (!r.ok) throw { response: { status: r.status } };
            return r.json();
        }),
    getRateLimitTiers: () =>
        fetch(`${API_BASE}/domain/rateLimitTiers`, { headers: authHeaders() })
            .then(r => {
                if (!r.ok) throw { response: { status: r.status } };
                return r.json();
            }),
};

const MAX_DOMAINS = 5;
const DEFAULT_RATE_LIMIT = 3600;

const isValidDomain = (val) => {
    const stripped = val.replace(/^https?:\/\//, "").replace(/\/$/, "");
    return /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(stripped);
};

const cleanDomain = (val) => val.replace(/^https?:\/\//, "").replace(/\/$/, "").toLowerCase().trim();

const S = {
    pageWrapper: { padding: "36px 24px 60px" },
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px", flexWrap: "wrap", gap: "16px" },
    pageTitle: { fontSize: "24px", fontWeight: "700", color: "var(--text-color)", margin: 0 },
    pageSubtitle: { fontSize: "13px", opacity: 0.5, margin: "4px 0 0" },
    card: { background: "var(--body-color-less)", borderRadius: "16px", border: "1px solid var(--body-color-less2)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: "24px" },
    cardHeader: { display: "flex", alignItems: "center", gap: "12px", padding: "18px 24px", borderBottom: "1px solid var(--body-color-less2)" },
    cardHeaderIcon: { width: "34px", height: "34px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "15px" },
    cardHeaderTitle: { fontSize: "15px", fontWeight: "700", color: "var(--text-color)", margin: 0 },
    cardHeaderSub: { fontSize: "12px", color: "var(--text-color)", opacity: 0.5, marginTop: "2px" },
    cardBody: { padding: "22px 24px" },
    label: { fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", color: "var(--text-color)", opacity: 0.7 },
    input: { padding: "10px 14px", fontSize: "14px", borderRadius: "10px", border: "1px solid var(--body-color-less2)", background: "var(--body-color)", color: "var(--text-color)", outline: "none", width: "100%", boxSizing: "border-box" },
    inputError: { padding: "10px 14px", fontSize: "14px", borderRadius: "10px", border: "1px solid #ef4444", background: "var(--body-color)", color: "var(--text-color)", outline: "none", width: "100%", boxSizing: "border-box" },
    btnPrimary: { padding: "10px 24px", background: "var(--btn-color)", color: "#fff", border: "none", borderRadius: "10px", fontSize: "13px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" },
    btnGhost: { padding: "8px 18px", background: "transparent", border: "1px solid var(--body-color-less2)", color: "var(--text-color)", borderRadius: "10px", fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" },
    btnDanger: { padding: "6px 14px", background: "rgba(239,68,68,0.1)", border: "none", color: "#ef4444", borderRadius: "8px", fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" },
    badge: { padding: "2px 8px", borderRadius: "6px", fontSize: "10px", fontWeight: "700", letterSpacing: "0.3px" },
    errorText: { fontSize: "11px", color: "#ef4444", display: "flex", alignItems: "center", gap: "4px" },
    helperText: { fontSize: "11px", color: "var(--text-color)", opacity: 0.45 },
};

function DeleteConfirmationModal({ domain, onConfirm, onCancel, isDeleting }) {
    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
            onClick={e => { if (e.target === e.currentTarget && !isDeleting) onCancel(); }}>
            <div style={{ background: "var(--body-color-less)", borderRadius: "18px", border: "1px solid var(--body-color-less2)", boxShadow: "0 24px 64px rgba(0,0,0,0.35)", width: "100%", maxWidth: "420px", overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "20px 24px", borderBottom: "1px solid var(--body-color-less2)" }}>
                    <div style={{ width: "38px", height: "38px", borderRadius: "11px", background: "rgba(239,68,68,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", fontSize: "18px", flexShrink: 0 }}>
                        <i className="mdi mdi-alert-circle-outline" />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "15px", fontWeight: "700", color: "var(--text-color)" }}>Delete Domain</div>
                        <div style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.5, marginTop: "1px" }}>This action cannot be undone</div>
                    </div>
                </div>
                <div style={{ padding: "24px" }}>
                    <div style={{ padding: "12px 16px", borderRadius: "10px", background: "color-mix(in srgb, #ef4444 6%, var(--body-color))", border: "1px solid color-mix(in srgb, #ef4444 20%, transparent)", display: "flex", gap: "10px", marginBottom: "22px" }}>
                        <i className="mdi mdi-information-outline" style={{ fontSize: "15px", color: "#ef4444", flexShrink: 0, marginTop: "1px" }} />
                        <div style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.7, lineHeight: "1.6" }}>
                            Are you sure you want to delete <strong style={{ opacity: 1 }}>{domain}</strong>? The domain will be removed from your whitelist immediately.
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                        <button onClick={onCancel} disabled={isDeleting} style={{ padding: "10px 24px", background: "transparent", border: "1px solid var(--body-color-less2)", color: "var(--text-color)", borderRadius: "10px", fontSize: "13px", fontWeight: "600", cursor: isDeleting ? "not-allowed" : "pointer", opacity: isDeleting ? 0.5 : 1 }}>Cancel</button>
                        <button onClick={onConfirm} disabled={isDeleting} style={{ padding: "10px 24px", background: "#ef4444", color: "#fff", border: "none", borderRadius: "10px", fontSize: "13px", fontWeight: "600", cursor: isDeleting ? "not-allowed" : "pointer", opacity: isDeleting ? 0.6 : 1, display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            {isDeleting ? <><i className="mdi mdi-loading mdi-spin" /> Deleting...</> : <><i className="mdi mdi-delete-outline" /> Delete</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function DeleteSuccessModal({ domain, onClose }) {
    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{ background: "var(--body-color-less)", borderRadius: "18px", border: "1px solid var(--body-color-less2)", boxShadow: "0 24px 64px rgba(0,0,0,0.35)", width: "100%", maxWidth: "420px", overflow: "hidden" }}>
                <div style={{ padding: "40px 24px 24px", textAlign: "center" }}>
                    <div style={{ width: "56px", height: "56px", borderRadius: "16px", background: "rgba(34,197,94,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: "26px", color: "#22c55e" }}>
                        <i className="mdi mdi-check-circle-outline" />
                    </div>
                    <div style={{ fontSize: "15px", fontWeight: "700", color: "var(--text-color)", marginBottom: "6px" }}>Domain Deleted</div>
                    <div style={{ fontSize: "13px", color: "var(--text-color)", opacity: 0.55, lineHeight: "1.6", marginBottom: "24px" }}>
                        Domain <strong style={{ opacity: 1 }}>{domain}</strong> has been successfully removed from your whitelist.
                    </div>
                    <button onClick={onClose} style={{ padding: "10px 24px", background: "var(--btn-color)", color: "#fff", border: "none", borderRadius: "10px", fontSize: "13px", fontWeight: "600", cursor: "pointer", margin: "0 auto" }}>Done</button>
                </div>
            </div>
        </div>
    );
}

function RateLimitRequestModal({ domain, merchantId, onClose, rateLimitTiers, currentRateLimitTierId, onRequestSuccess }) {
    const [sent, setSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [selectedTierId, setSelectedTierId] = useState("");
    const [tierError, setTierError] = useState("");

    const handleSend = async () => {
        if (!selectedTierId || selectedTierId.trim() === "") {
            setTierError("Please select a rate limit tier");
            return;
        }

        const selectedTier = rateLimitTiers?.find(t => t.id.toString() === selectedTierId);
        if (!selectedTier) {
            setTierError("Invalid tier selected");
            return;
        }

        setSending(true);
        try {
            const response = await fetch(
                `${API_BASE}/domain/request/increaseRateLimit`,
                {
                    method: "PUT",
                    headers: authHeaders(),
                    body: JSON.stringify({
                        merchantId,
                        requestStatus: "1",
                        rateLimit: selectedTier.rateLimit,
                        requestedId: selectedTier.id,
                        domain: `https://${domain}`,
                    }),
                }
            );
            if (!response.ok) throw { response: { status: response.status } };
            const result = await response.json();
            if (result.error?.error_data === 0 || result.error === 0) {
                setSending(false);
                setSent(true);
                onRequestSuccess?.(domain);
                showToast("success", "Rate limit increase request submitted successfully");
            } else {
                throw new Error(result.error?.error_message || "Request failed");
            }
        } catch (err) {
            setSending(false);
            showHttpError(err);
        }
    };

    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{ background: "var(--body-color-less)", borderRadius: "18px", border: "1px solid var(--body-color-less2)", boxShadow: "0 24px 64px rgba(0,0,0,0.35)", width: "100%", maxWidth: "480px", overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "20px 24px", borderBottom: "1px solid var(--body-color-less2)" }}>
                    <div style={{ width: "38px", height: "38px", borderRadius: "11px", background: "color-mix(in srgb, var(--btn-color) 12%, transparent)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--btn-color)", fontSize: "18px", flexShrink: 0 }}>
                        <i className="mdi mdi-speedometer" />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "15px", fontWeight: "700", color: "var(--text-color)" }}>Request Rate Limit Increase</div>
                        <div style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.5, marginTop: "1px" }}>An email will be sent to PayBito support</div>
                    </div>
                    <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-color)", opacity: 0.4, padding: "4px", display: "flex", alignItems: "center" }}>
                        <i className="mdi mdi-close" style={{ fontSize: "18px" }} />
                    </button>
                </div>
                <div style={{ padding: "24px" }}>
                    {!sent ? (
                        <>
                            <div style={{ marginBottom: "22px" }}>
                                <label style={{ fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", color: "var(--text-color)", opacity: 0.7, display: "block", marginBottom: "8px" }}>Select Rate Limit Tier</label>
                                <div style={{ position: "relative" }}>
                                    <i className="mdi mdi-speedometer" style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "15px", color: "var(--text-color)", opacity: 0.35, pointerEvents: "none", zIndex: 1 }} />
                                    <select
                                        value={selectedTierId}
                                        onChange={e => { setSelectedTierId(e.target.value); setTierError(""); }}
                                        style={{ ...S.input, paddingLeft: "38px", borderColor: tierError ? "#ef4444" : "var(--body-color-less2)", appearance: "none", paddingRight: "32px" }}
                                    >
                                        <option value="">Choose a tier...</option>
                                        {(rateLimitTiers || []).map(tier => (
                                            <option key={tier.id} value={tier.id}>
                                                {tier.name} - {tier.rateLimit.toLocaleString()}/hr
                                            </option>
                                        ))}
                                    </select>
                                    <i className="mdi mdi-chevron-down" style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "18px", color: "var(--text-color)", opacity: 0.35, pointerEvents: "none" }} />
                                </div>
                                {tierError && <div style={{ fontSize: "11px", color: "#ef4444", display: "flex", alignItems: "center", gap: "4px", marginTop: "6px" }}><i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />{tierError}</div>}
                                {selectedTierId && (
                                    <div style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.45, marginTop: "6px" }}>
                                        {rateLimitTiers?.find(t => t.id.toString() === selectedTierId) && `Selected: ${rateLimitTiers.find(t => t.id.toString() === selectedTierId).name} - ${rateLimitTiers.find(t => t.id.toString() === selectedTierId).rateLimit.toLocaleString()}/hr`}
                                    </div>
                                )}
                            </div>

                            <div style={{ padding: "12px 16px", borderRadius: "10px", background: "color-mix(in srgb, #f59e0b 6%, var(--body-color))", border: "1px solid color-mix(in srgb, #f59e0b 20%, transparent)", display: "flex", gap: "10px", marginBottom: "22px" }}>
                                <i className="mdi mdi-information-outline" style={{ fontSize: "15px", color: "#f59e0b", flexShrink: 0, marginTop: "1px" }} />
                                <div style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.7, lineHeight: "1.6" }}>
                                    Request for domain <strong style={{ opacity: 1 }}>{domain}</strong>. The support team will review and respond within 1-2 business days.
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                <button onClick={onClose} style={S.btnGhost}>Cancel</button>
                                <button onClick={handleSend} disabled={sending} style={{ ...S.btnPrimary, cursor: sending ? "not-allowed" : "pointer", opacity: sending ? 0.6 : 1 }}>
                                    {sending ? <><i className="mdi mdi-loading mdi-spin" /> Submitting...</> : <><i className="mdi mdi-send-outline" /> Submit Request</>}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div style={{ textAlign: "center", padding: "12px 0 8px" }}>
                            <div style={{ width: "56px", height: "56px", borderRadius: "16px", background: "rgba(34,197,94,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: "26px", color: "#22c55e" }}>
                                <i className="mdi mdi-check-circle-outline" />
                            </div>
                            <div style={{ fontSize: "15px", fontWeight: "700", color: "var(--text-color)", marginBottom: "6px" }}>Request Submitted</div>
                            <div style={{ fontSize: "13px", color: "var(--text-color)", opacity: 0.55, lineHeight: "1.6", marginBottom: "22px" }}>
                                Your rate limit increase request has been submitted successfully. The support team will review and respond within 1-2 business days.
                            </div>
                            <button onClick={onClose} style={{ ...S.btnPrimary, margin: "0 auto" }}>Done</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function DomainRow({ id, domain, index, onDelete, onRequestIncrease, isReadOnly, description, status, isIncreaseRequested, rateLimitTierId, rateLimitTiers }) {
    const currentTier = rateLimitTiers?.find(t => t.id === rateLimitTierId);
    const statusColors = {
        APPROVED: { bg: "rgba(34,197,94,0.1)", text: "#22c55e" },
        PENDING: { bg: "rgba(245,158,11,0.1)", text: "#f59e0b" },
        REJECTED: { bg: "rgba(239,68,68,0.1)", text: "#ef4444" },
    };
    const statusColor = statusColors[status] || statusColors.PENDING;

    return (
        <div className="dw-domain-row" style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "13px 16px", borderRadius: "12px", background: "var(--body-color)", border: "1px solid var(--body-color-less2)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div className="dw-domain-index" style={{ width: "26px", height: "26px", borderRadius: "8px", background: "color-mix(in srgb, var(--btn-color) 12%, transparent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "700", color: "var(--btn-color)", flexShrink: 0 }}>
                    {index + 1}
                </div>
                <div className="dw-domain-name" style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <i className="mdi mdi-web" style={{ fontSize: "13px", color: "var(--btn-color)", opacity: 0.7 }} />
                        <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-color)", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{domain}</span>
                    </div>
                </div>
                <div className="dw-domain-rate" style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 12px", borderRadius: "20px", background: "color-mix(in srgb, #22c55e 8%, transparent)", border: "1px solid color-mix(in srgb, #22c55e 20%, transparent)", flexShrink: 0 }}>
                    <i className="mdi mdi-speedometer" style={{ fontSize: "12px", color: "#22c55e" }} />
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#22c55e" }} title={currentTier?.name}>
                        {currentTier ? `${currentTier.rateLimit.toLocaleString()}/hr` : `${DEFAULT_RATE_LIMIT.toLocaleString()}/hr`}
                    </span>
                </div>
                <div style={{ ...statusColor, padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", flexShrink: 0 }}>
                    {status || "PENDING"} 
                </div>
            </div>

            {description && (
                <div style={{ padding: "8px 12px", borderRadius: "8px", background: "var(--body-color-less)", borderLeft: "2px solid var(--btn-color)" }}>
                    <div style={{ fontSize: "11px", fontWeight: "600", color: "var(--text-color)", opacity: 0.6, marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.3px" }}>Description</div>
                    <div style={{ fontSize: "12px", color: "var(--text-color)", wordBreak: "break-word" }}>{description}</div>
                </div>
            )}

            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                {status === "APPROVED" && (
                    <button className="dw-domain-increase" onClick={() => onRequestIncrease(domain)} disabled={isReadOnly || isIncreaseRequested === 1}
                        style={{ padding: "5px 12px", borderRadius: "8px", border: `1px solid color-mix(in srgb, ${isIncreaseRequested === 1 ? "#f59e0b" : "var(--btn-color)"} 30%, transparent)`, background: `color-mix(in srgb, ${isIncreaseRequested === 1 ? "#f59e0b" : "var(--btn-color)"} 6%, transparent)`, color: isIncreaseRequested === 1 ? "#f59e0b" : "var(--btn-color)", fontSize: "11px", fontWeight: "600", cursor: (isReadOnly || isIncreaseRequested === 1) ? "not-allowed" : "pointer", opacity: (isReadOnly || isIncreaseRequested === 1) ? 0.5 : 1, display: "inline-flex", alignItems: "center", gap: "5px", flexShrink: 0, whiteSpace: "nowrap" }}>
                        <i className={`mdi ${isIncreaseRequested === 1 ? "mdi-clock-outline" : "mdi-arrow-up-circle-outline"}`} style={{ fontSize: "13px" }} /> {isIncreaseRequested === 1 ? "Already Requested" : "Request Increase"}
                    </button>
                )}
                <button className="dw-domain-delete" onClick={() => onDelete(id, domain)} disabled={isReadOnly} style={{ ...S.btnDanger, padding: "6px 10px", flexShrink: 0, cursor: isReadOnly ? "not-allowed" : "pointer", opacity: isReadOnly ? 0.5 : 1 }}>
                    <i className="mdi mdi-delete-outline" style={{ fontSize: "14px" }} />
                </button>
            </div>
        </div>
    );
}

export default function DomainWhitelisting() {
    const merchantId = getMerchantId();
    const [domains, setDomains] = useState([]);
    const [rateLimitTiers, setRateLimitTiers] = useState([]);
    const [inputVal, setInputVal] = useState("");
    const [descriptionVal, setDescriptionVal] = useState("");
    const [inputError, setInputError] = useState("");
    const [adding, setAdding] = useState(false);
    const [modalDomain, setModalDomain] = useState(null);
    const [modalDomainTierId, setModalDomainTierId] = useState(null);
    const [deleteConfirmDomain, setDeleteConfirmDomain] = useState(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteSuccessDomain, setDeleteSuccessDomain] = useState(null);
    const atMax = domains.length >= MAX_DOMAINS;
    const isReadOnly = currentPageAccess() === "READ";
    const readOnlyBlock = () => { showToast("error", "You have read-only access to this page"); };

    useEffect(() => {
        api.getRateLimitTiers()
            .then(res => {
                if (res.success || res.error === 0) {
                    setRateLimitTiers(res.data || []);
                }
            })
            .catch(err => showHttpError(err));

        fetch(`${API_BASE}/domain/${merchantId}`, { headers: authHeaders() })
            .then(r => { if (!r.ok) throw { response: { status: r.status } }; return r.json(); })
            .then(res => {
                if (res.error !== 0) return;
                setDomains((res.data || []).map(d => ({
                    id: d.id,
                    domain: cleanDomain(d.merchantDomain),
                    description: d.description,
                    status: d.status,
                    isIncreaseRequested: d.isIncreaseRequested,
                    rateLimitTierId: d.rateLimitTierId,
                })));
            })
            .catch(err => showHttpError(err));
    }, [merchantId]);

    const addDomain = async () => {
        if (isReadOnly) { readOnlyBlock(); return; }
        if (/^https?:\/\//.test(inputVal.trim())) { setInputError("Please enter domain without http:// or https:// — e.g. app.yoursite.com"); return; }
        const cleaned = cleanDomain(inputVal);
        if (!cleaned) { setInputError("Please enter a domain name"); return; }
        if (!isValidDomain(cleaned)) { setInputError("Enter a valid domain (e.g. app.yoursite.com)"); return; }
        if (domains.some(d => d.domain === cleaned)) { setInputError("This domain has already been added"); return; }
        if (atMax) { setInputError(`You can add a maximum of ${MAX_DOMAINS} domains`); return; }
        setAdding(true);
        try {
            const res = await api.addDomain(merchantId, `https://${cleaned}`, descriptionVal);
            if (res.error !== 0) { setInputError(res.message || "Failed to add domain."); return; }
            const newDomain = {
                id: res.data?.id,
                domain: cleaned,
                description: descriptionVal,
                status: "PENDING",
                isIncreaseRequested: 0,
                rateLimitTierId: null,
            };
            setDomains(prev => [...prev, newDomain]);
            setInputVal(""); setDescriptionVal(""); setInputError("");
            showToast("success", `Domain "${cleaned}" whitelisted successfully.`);
        } catch (err) { showHttpError(err); }
        finally { setAdding(false); }
    };

    const handleKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); addDomain(); } };

    const deleteDomain = (id, domain) => {
        if (isReadOnly) { readOnlyBlock(); return; }
        setDeleteConfirmId(id);
        setDeleteConfirmDomain(domain);
    };

    const confirmDeleteDomain = async () => {
        if (!deleteConfirmId) return;
        setIsDeleting(true);
        try {
            const res = await api.cancelDomain(deleteConfirmId, merchantId);
            if (res.error !== 0) {
                showToast("error", res.message || "Failed to delete domain");
                setIsDeleting(false);
                setDeleteConfirmDomain(null);
                setDeleteConfirmId(null);
                return;
            }
            setDomains(prev => prev.filter(d => d.id !== deleteConfirmId));
            setDeleteConfirmDomain(null);
            setDeleteConfirmId(null);
            setIsDeleting(false);
            setDeleteSuccessDomain(deleteConfirmDomain);
        } catch (err) {
            setIsDeleting(false);
            showHttpError(err);
            setDeleteConfirmDomain(null);
            setDeleteConfirmId(null);
        }
    };
    const handleRequestIncrease = (domain) => {
        if (isReadOnly) { readOnlyBlock(); return; }
        const currentDomain = domains.find(d => d.domain === domain);
        setModalDomain(domain);
        setModalDomainTierId(currentDomain?.rateLimitTierId || null);
    };

    const handleRequestSuccess = async (domain) => {
        try {
            const response = await fetch(`${API_BASE}/domain/${merchantId}`, { headers: authHeaders() });
            if (!response.ok) throw { response: { status: response.status } };
            const res = await response.json();
            if (res.error === 0) {
                setDomains((res.data || []).map(d => ({
                    id: d.id,
                    domain: cleanDomain(d.merchantDomain),
                    description: d.description,
                    status: d.status,
                    isIncreaseRequested: d.isIncreaseRequested,
                    rateLimitTierId: d.rateLimitTierId,
                })));
            }
        } catch (err) {
            showHttpError(err);
        }
    };

    return (
        <Fragment>
            <style>{`
                @media (max-width: 768px) {
                    .dw-add-row > div:first-child {
                        flex-direction: column !important;
                    }
                    .dw-add-row > div:first-child > div:first-child,
                    .dw-add-row > div:first-child > button {
                        width: 100% !important;
                    }
                }
                @media (max-width: 576px) {
                    .dw-page-wrapper { padding: 20px 14px 40px !important; }
                    .dw-page-title { font-size: 20px !important; }
                    .dw-card-header { flex-wrap: wrap !important; padding: 16px !important; }
                    .dw-card-header-badges { flex-wrap: wrap !important; gap: 6px !important; }
                    .dw-card-body { padding: 16px !important; }
                    .dw-list-header { flex-direction: column !important; align-items: flex-start !important; gap: 6px !important; }
                    .dw-domain-row > div:first-child { flex-wrap: wrap !important; gap: 8px !important; }
                }
            `}</style>
            {modalDomain && (
                <RateLimitRequestModal domain={modalDomain} merchantId={merchantId} onClose={() => { setModalDomain(null); setModalDomainTierId(null); }} rateLimitTiers={rateLimitTiers} currentRateLimitTierId={modalDomainTierId} onRequestSuccess={handleRequestSuccess} />
            )}
            {deleteConfirmDomain && (
                <DeleteConfirmationModal domain={deleteConfirmDomain} onConfirm={confirmDeleteDomain} onCancel={() => { setDeleteConfirmDomain(null); setDeleteConfirmId(null); }} isDeleting={isDeleting} />
            )}
            {deleteSuccessDomain && (
                <DeleteSuccessModal domain={deleteSuccessDomain} onClose={() => setDeleteSuccessDomain(null)} />
            )}
            <div className="container-scroller">
                <Sidebar />
                <Header />
                <div className="main-panel">
                    <div className="dw-page-wrapper" style={S.pageWrapper}>

                        <div style={S.headerRow}>
                            <div>
                                <h2 className="dw-page-title" style={S.pageTitle}>Domain Whitelisting</h2>
                                <div style={S.pageSubtitle}>Restrict API access to specific domains</div>
                            </div>
                        </div>

                        <div style={S.card}>
                            <div className="dw-card-header" style={S.cardHeader}>
                                <div style={{ ...S.cardHeaderIcon, background: "color-mix(in srgb, #10b981 12%, transparent)", color: "#10b981" }}>
                                    <i className="mdi mdi-shield-check-outline" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div className="dw-card-header-badges" style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                        <h3 style={S.cardHeaderTitle}>Whitelisted Domains</h3>
                                        <span style={{ ...S.badge, background: "color-mix(in srgb, #10b981 12%, transparent)", color: "#10b981" }}>Security</span>
                                        <span style={{ ...S.badge, background: domains.length >= MAX_DOMAINS ? "rgba(239,68,68,0.1)" : "rgba(34,197,94,0.1)", color: domains.length >= MAX_DOMAINS ? "#ef4444" : "#22c55e" }}>
                                            {domains.length} / {MAX_DOMAINS} domains
                                        </span>
                                    </div>
                                    <div style={S.cardHeaderSub}>Max {MAX_DOMAINS} domains per merchant</div>
                                </div>
                            </div>

                            <div className="dw-card-body" style={S.cardBody}>
                                <div style={{ display: "flex", gap: "10px", padding: "12px 16px", borderRadius: "10px", background: "color-mix(in srgb, #10b981 6%, var(--body-color))", border: "1px solid color-mix(in srgb, #10b981 18%, transparent)", marginBottom: "22px" }}>
                                    <i className="mdi mdi-information-outline" style={{ fontSize: "16px", color: "#10b981", flexShrink: 0, marginTop: "1px" }} />
                                    <div style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.7, lineHeight: "1.7" }}>
                                        Only requests originating from whitelisted domains will be accepted by the payment API. Each domain has a default rate limit of <strong style={{ color: "#10b981", opacity: 1 }}>{DEFAULT_RATE_LIMIT.toLocaleString()} requests/hour</strong>. To request a higher limit, click <strong style={{ opacity: 1 }}>Request Increase</strong> on any domain row.
                                    </div>
                                </div>

                                {/* Add domain input */}
                                <div style={{ marginBottom: "20px" }}>
                                    <label style={{ ...S.label, display: "block", marginBottom: "8px" }}>Add Domain</label>
                                    <div className="dw-add-row" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                        <div style={{ display: "flex", gap: "10px" }}>
                                            <div style={{ flex: 1, position: "relative" }}>
                                                <i className="mdi mdi-web" style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "15px", color: "var(--text-color)", opacity: 0.35, pointerEvents: "none" }} />
                                                <input type="text"
                                                    style={{ ...(inputError ? S.inputError : S.input), paddingLeft: "38px", opacity: (atMax || isReadOnly) ? 0.5 : 1 }}
                                                    value={inputVal}
                                                    onChange={e => { setInputVal(e.target.value); setInputError(""); }}
                                                    onKeyDown={handleKeyDown}
                                                    placeholder={isReadOnly ? "Read-only access" : (atMax ? `Maximum ${MAX_DOMAINS} domains reached` : "e.g. app.yoursite.com")}
                                                    disabled={atMax || adding || isReadOnly}
                                                />
                                            </div>
                                            <button className="dw-add-btn" onClick={addDomain} disabled={atMax || adding || isReadOnly}
                                                style={{ ...S.btnPrimary, opacity: (atMax || adding || isReadOnly) ? 0.45 : 1, cursor: (atMax || adding || isReadOnly) ? "not-allowed" : "pointer", flexShrink: 0 }}>
                                                {adding ? <><i className="mdi mdi-loading mdi-spin" /> Adding...</> : <><i className="mdi mdi-plus" /> Add Domain</>}
                                            </button>
                                        </div>
                                        <div style={{ position: "relative" }}>
                                            <i className="mdi mdi-note-text-outline" style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "15px", color: "var(--text-color)", opacity: 0.35, pointerEvents: "none" }} />
                                            <input type="text"
                                                style={{ ...S.input, paddingLeft: "38px", opacity: (atMax || isReadOnly) ? 0.5 : 1 }}
                                                value={descriptionVal}
                                                onChange={e => { setDescriptionVal(e.target.value); setInputError(""); }}
                                                placeholder={isReadOnly ? "Read-only access" : "Add description"}
                                                disabled={atMax || adding || isReadOnly}
                                            />
                                        </div>
                                    </div>
                                    {inputError && <div style={{ ...S.errorText, marginTop: "6px" }}><i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />{inputError}</div>}
                                    {!inputError && <div style={{ ...S.helperText, marginTop: "6px" }}>Enter domain without http:// or https:// — e.g. <code style={{ fontSize: "11px", background: "var(--body-color-less2)", padding: "1px 5px", borderRadius: "4px" }}>app.yoursite.com</code>. Press Enter or click Add.</div>}
                                </div>

                                {/* Domain list */}
                                {domains.length > 0 ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "22px" }}>
                                        <div className="dw-list-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                                            <div style={{ fontSize: "12px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.3px", color: "var(--text-color)", opacity: 0.5, display: "flex", alignItems: "center", gap: "6px" }}>
                                                <i className="mdi mdi-format-list-bulleted" style={{ fontSize: "13px" }} /> Whitelisted Domains
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "var(--text-color)", opacity: 0.45 }}>
                                                <i className="mdi mdi-speedometer" style={{ fontSize: "13px" }} />
                                                Default: {DEFAULT_RATE_LIMIT.toLocaleString()} req/hr per domain
                                            </div>
                                        </div>
                                        {domains.map((d, i) => (
                                            <DomainRow key={d.domain} id={d.id} domain={d.domain} description={d.description} status={d.status} index={i} onDelete={deleteDomain} onRequestIncrease={handleRequestIncrease} isReadOnly={isReadOnly} isIncreaseRequested={d.isIncreaseRequested} rateLimitTierId={d.rateLimitTierId} rateLimitTiers={rateLimitTiers} />
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{ textAlign: "center", padding: "36px 24px", borderRadius: "12px", background: "var(--body-color)", border: "1px dashed var(--body-color-less2)", marginBottom: "22px" }}>
                                        <div style={{ width: "48px", height: "48px", borderRadius: "14px", background: "color-mix(in srgb, #10b981 10%, transparent)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", fontSize: "22px", color: "#10b981" }}>
                                            <i className="mdi mdi-shield-off-outline" />
                                        </div>
                                        <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--text-color)", marginBottom: "5px" }}>No domains whitelisted</div>
                                        <div style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.5 }}>Add at least one domain to restrict API access</div>
                                    </div>
                                )}

                                {/* Slot progress bar */}
                                <div style={{ padding: "14px 16px", borderRadius: "10px", background: "var(--body-color)", border: "1px solid var(--body-color-less2)" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                        <span style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-color)", opacity: 0.6 }}>Domain slots used</span>
                                        <span style={{ fontSize: "12px", fontWeight: "700", color: domains.length >= MAX_DOMAINS ? "#ef4444" : "var(--text-color)" }}>{domains.length} / {MAX_DOMAINS}</span>
                                    </div>
                                    <div style={{ height: "6px", borderRadius: "3px", background: "var(--body-color-less2)", overflow: "hidden" }}>
                                        <div style={{ height: "100%", borderRadius: "3px", width: `${(domains.length / MAX_DOMAINS) * 100}%`, background: domains.length >= MAX_DOMAINS ? "#ef4444" : domains.length >= MAX_DOMAINS - 1 ? "#f59e0b" : "#10b981", transition: "width 0.3s, background 0.3s" }} />
                                    </div>
                                    {atMax && (
                                        <div style={{ marginTop: "8px", fontSize: "11px", color: "#ef4444", display: "flex", alignItems: "center", gap: "5px" }}>
                                            <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "12px" }} />
                                            Maximum domain limit reached. Remove an existing domain to add a new one.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                    <Footer />
                </div>
            </div>
        </Fragment>
    );
}
